/**
 * BulkVerifyProgress (Task 5.7) — dashboard polling progress strip for a
 * bulk verification job.
 *
 * Mounted on the issues list page (Task 5.8) when a `dashboard_bulk` job is
 * active. Polls `GET /verifications/jobs/:jobId` every 3s, renders progress
 * + per-status counts, and surfaces a recovery hint when the extension
 * disconnects mid-run (status='paused' + cancelReason='extension_disconnected').
 *
 * View-only — does NOT trigger captures (that's the extension's job). The
 * three actions are:
 *   - See completed issues  -> consumer-supplied navigation (`onViewResults`)
 *   - Resume                -> POST /verifications/jobs/:id/resume (paused only)
 *   - Cancel                -> DELETE /verifications/jobs/:id (active jobs)
 *   - Dismiss               -> close the strip without server side-effects
 *
 * Styling uses dashboard tokens per CLAUDE.md UI guidelines: `card-sm`,
 * `primary-*` (not `blue-*`), gray-100/200 dividers. No emoji.
 *
 * Polling lifecycle:
 *   - Mount: fire one fetch immediately
 *   - Each successful fetch: schedule next tick 3s later UNLESS the job has
 *     reached a terminal state (completed/cancelled)
 *   - Unmount: clear the pending timer and mark the closure dead so a
 *     late-arriving response can't setState on an unmounted component
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { verifyApi, type GetJobResponse } from '../../lib/api/verify';
import { log } from '../../lib/logger';

export interface BulkVerifyProgressProps {
  jobId: string;
  onDismiss: () => void;
  onViewResults: () => void;
}

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES: ReadonlyArray<GetJobResponse['status']> = ['completed', 'cancelled'];

function isTerminal(status: GetJobResponse['status']): boolean {
  return TERMINAL_STATUSES.includes(status);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Unexpected error';
}

export function BulkVerifyProgress({
  jobId,
  onDismiss,
  onViewResults,
}: BulkVerifyProgressProps): JSX.Element | null {
  const { getToken } = useAuth();
  const [job, setJob] = useState<GetJobResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<'resume' | 'cancel' | null>(null);

  // Debug log for tracing render state (gated to dev via the dashboard logger).
  log.info('[BulkVerifyProgress] render', {
    jobId,
    hasJob: !!job,
    status: job?.status,
    hasError: !!error,
  });

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const token = await getToken();
        const next = await verifyApi.getJob(token ?? undefined, jobId);
        if (!alive) return;
        log.info('[BulkVerifyProgress] tick result', {
          jobId,
          status: next.status,
          completed: next.completedIssues,
          total: next.totalIssues,
        });
        setJob(next);
        setError(null);
        if (isTerminal(next.status)) {
          log.info('[BulkVerifyProgress] terminal status reached, stopping poll', {
            jobId,
            status: next.status,
          });
          return;
        }
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e: unknown) {
        if (!alive) return;
        const msg = getErrorMessage(e);
        log.error('[BulkVerifyProgress] tick failed', { jobId, error: msg });
        setError(msg);
        // On error, retry on the same interval — transient network blips
        // shouldn't kill the strip. The error banner stays visible until the
        // next successful tick clears it.
        timer = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    void tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, getToken]);

  const handleResume = async () => {
    setActionLoading('resume');
    try {
      const token = await getToken();
      await verifyApi.resumeJob(token ?? undefined, jobId);
      log.info('[BulkVerifyProgress] resume requested', { jobId });
    } catch (e: unknown) {
      log.error('[BulkVerifyProgress] resume failed', { jobId, error: getErrorMessage(e) });
      setError(getErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    setActionLoading('cancel');
    try {
      const token = await getToken();
      await verifyApi.cancelJob(token ?? undefined, jobId);
      log.info('[BulkVerifyProgress] cancel requested', { jobId });
      onDismiss();
    } catch (e: unknown) {
      log.error('[BulkVerifyProgress] cancel failed', { jobId, error: getErrorMessage(e) });
      setError(getErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  // Initial render before the first fetch returns. Don't flash an empty card.
  if (!job && !error) return null;

  if (error && !job) {
    return (
      <section className="card-sm mb-4" aria-labelledby="bulk-progress-error-title">
        <h3 id="bulk-progress-error-title" className="text-sm font-semibold text-red-700 mb-1">
          Couldn't load verification job
        </h3>
        <p className="text-sm text-gray-700 mb-3">{error}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-gray-500 underline hover:text-gray-700"
        >
          <span className="text-cap-trim block">Dismiss</span>
        </button>
      </section>
    );
  }

  // Narrowed: job is non-null past this point.
  if (!job) return null;

  const percent =
    job.totalIssues > 0 ? Math.round((job.completedIssues / job.totalIssues) * 100) : 0;

  const isActive = !isTerminal(job.status);
  const isPaused = job.status === 'paused';
  const showExtensionDisconnectedHint =
    isPaused && job.cancelReason === 'extension_disconnected';

  return (
    <section className="card-sm mb-4" aria-labelledby="bulk-progress-title">
      <div className="flex items-center justify-between mb-3">
        <h3 id="bulk-progress-title" className="text-sm font-semibold text-gray-900">
          Verification job
        </h3>
        <span className="text-xs text-gray-500">Status: {job.status}</span>
      </div>

      <div
        className="h-2 bg-gray-100 rounded overflow-hidden mb-2"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Verification progress"
      >
        <div
          className="h-full bg-primary-500 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="text-sm text-gray-700 mb-3">
        {job.completedIssues} / {job.totalIssues} done
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs mb-3 border-t border-gray-100 pt-3">
        <Counter label="Passed" value={job.passedCount} color="text-green-700" />
        <Counter label="Caveat" value={job.caveatCount} color="text-amber-700" />
        <Counter label="Failed" value={job.failedCount} color="text-red-700" />
        <Counter label="Inconclusive" value={job.inconclusiveCount} color="text-gray-700" />
        <Counter label="Error" value={job.errorCount} color="text-red-700" />
      </div>

      {showExtensionDisconnectedHint && (
        <div className="bg-amber-50 text-amber-700 text-xs rounded p-2 mb-3">
          The verification stalled. If you navigated to a restricted page (chrome://, file://,
          or an extension store), reopen{' '}
          <code className="font-mono">{job.currentUrl ?? 'the next URL'}</code> in a regular
          browser tab to resume.
        </div>
      )}

      {error && job && (
        <div className="bg-red-50 text-red-700 text-xs rounded p-2 mb-3">
          Failed to refresh: {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          onClick={onViewResults}
          className="text-xs text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-md font-medium"
        >
          <span className="text-cap-trim block">See completed issues</span>
        </button>
        {isPaused && (
          <button
            type="button"
            onClick={handleResume}
            disabled={actionLoading !== null}
            className="text-xs bg-white border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-cap-trim block">{actionLoading === 'resume' ? 'Resuming...' : 'Resume'}</span>
          </button>
        )}
        {isActive && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={actionLoading !== null}
            className="text-xs text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-cap-trim block">{actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel'}</span>
          </button>
        )}
        {!isActive && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-md font-medium"
          >
            <span className="text-cap-trim block">Dismiss</span>
          </button>
        )}
      </div>
    </section>
  );
}

interface CounterProps {
  label: string;
  value: number;
  color: string;
}

function Counter({ label, value, color }: CounterProps): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="text-gray-500 text-[10px] uppercase tracking-wide">{label}</span>
      <span className={`text-base font-semibold ${color}`}>{value}</span>
    </div>
  );
}
